import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import { useAuth } from '../../contexts/AuthContext'

// ── Types ──────────────────────────────────────────────────────────────────────

interface Category {
  id: string
  name: string
  slug: string
  parentId: string | null
  isActive: boolean
  sortOrder: number
}

interface AdminStore {
  id: string
  name: string
  address: string
  status: 'pending' | 'active' | 'suspended'
  product_count?: number
  order_count?: number
}

type StoreStatus = 'all' | 'pending' | 'active' | 'suspended'
type Tab = 'categories' | 'stores'

// ── Categories tab ─────────────────────────────────────────────────────────────

function CategoriesTab() {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name: '', slug: '', parentId: '' })
  const [saving, setSaving] = useState(false)

  function loadCategories() {
    return api.get<Category[]>('/api/admin/categories')
      .then(setCategories)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadCategories() }, [])

  function handleNameChange(name: string) {
    const slug = name.toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim().replace(/\s+/g, '-')
    setForm(f => ({ ...f, name, slug }))
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/api/admin/categories', {
        name: form.name,
        slug: form.slug,
        parentId: form.parentId || null,
      })
      setForm({ name: '', slug: '', parentId: '' })
      setShowForm(false)
      setLoading(true)
      await loadCategories()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al crear categoría')
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(cat: Category) {
    await api.patch(`/api/admin/categories/${cat.id}`, { isActive: !cat.isActive })
    setCategories(cs => cs.map(c => c.id === cat.id ? { ...c, isActive: !c.isActive } : c))
  }

  async function handleDelete(id: string) {
    if (!confirm('¿Desactivar esta categoría?')) return
    await api.delete(`/api/admin/categories/${id}`)
    setCategories(cs => cs.map(c => c.id === id ? { ...c, isActive: false } : c))
  }

  const roots = categories.filter(c => !c.parentId)
  const children = (parentId: string) => categories.filter(c => c.parentId === parentId)

  return (
    <>
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Categorías Globales</h1>
          <p className="admin-sub">Árbol de categorías asignado a los productos del marketplace</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowForm(s => !s)}
          data-testid="btn-new-category"
        >
          {showForm ? 'Cancelar' : '+ Nueva categoría'}
        </button>
      </div>

      {showForm && (
        <form className="category-form" onSubmit={handleCreate} data-testid="category-form">
          <h3>Nueva categoría</h3>
          <div className="field-row">
            <div className="field">
              <label htmlFor="cat-name">Nombre</label>
              <input
                id="cat-name"
                type="text"
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
                required
                data-testid="input-cat-name"
              />
            </div>
            <div className="field">
              <label htmlFor="cat-slug">Slug</label>
              <input
                id="cat-slug"
                type="text"
                value={form.slug}
                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                required
                pattern="[a-z0-9-]+"
                data-testid="input-cat-slug"
              />
            </div>
            <div className="field">
              <label htmlFor="cat-parent">Categoría padre (opcional)</label>
              <select
                id="cat-parent"
                value={form.parentId}
                onChange={e => setForm(f => ({ ...f, parentId: e.target.value }))}
              >
                <option value="">— Categoría raíz —</option>
                {roots.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            data-testid="btn-save-category"
          >
            {saving ? 'Guardando...' : 'Guardar categoría'}
          </button>
        </form>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Cargando categorías...</div>
      ) : categories.length === 0 ? (
        <div className="empty-state" data-testid="empty-categories">
          <div className="empty-icon">📂</div>
          <p>No hay categorías. Crea la primera.</p>
        </div>
      ) : (
        <div className="category-tree" data-testid="category-list">
          {roots.map(root => (
            <div key={root.id} className="cat-group">
              <div className={`cat-row cat-root ${!root.isActive ? 'cat-inactive' : ''}`}>
                <span className="cat-name">{root.name}</span>
                <span className="cat-slug">{root.slug}</span>
                <div className="cat-actions">
                  <button
                    type="button"
                    className={`badge ${root.isActive ? 'badge-green' : 'badge-gray'}`}
                    onClick={() => toggleActive(root)}
                    title="Cambiar estado"
                  >
                    {root.isActive ? 'Activa' : 'Inactiva'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-danger"
                    onClick={() => handleDelete(root.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {children(root.id).map(child => (
                <div key={child.id} className={`cat-row cat-child ${!child.isActive ? 'cat-inactive' : ''}`}>
                  <span className="cat-name">↳ {child.name}</span>
                  <span className="cat-slug">{child.slug}</span>
                  <div className="cat-actions">
                    <button
                      type="button"
                      className={`badge ${child.isActive ? 'badge-green' : 'badge-gray'}`}
                      onClick={() => toggleActive(child)}
                    >
                      {child.isActive ? 'Activa' : 'Inactiva'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-danger"
                      onClick={() => handleDelete(child.id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ── Stores tab ─────────────────────────────────────────────────────────────────

function StoresTab() {
  const [stores, setStores] = useState<AdminStore[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState<StoreStatus>('all')

  function loadStores(status: StoreStatus) {
    setLoading(true)
    const qs = status !== 'all' ? `?status=${status}` : ''
    api.get<AdminStore[]>(`/api/admin/stores${qs}`)
      .then(setStores)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadStores(statusFilter) }, [statusFilter])

  async function updateStatus(id: string, status: 'active' | 'suspended') {
    try {
      await api.patch(`/api/admin/stores/${id}/status`, { status })
      setStores(ss => ss.map(s => s.id === id ? { ...s, status } : s))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al actualizar')
    }
  }

  const statusLabel = (s: AdminStore['status']) => {
    if (s === 'active') return <span className="badge badge-green">Activa</span>
    if (s === 'pending') return <span className="badge badge-yellow">Pendiente</span>
    return <span className="badge badge-red">Suspendida</span>
  }

  return (
    <>
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Ferreterías</h1>
          <p className="admin-sub">Gestión de tiendas registradas en el marketplace</p>
        </div>
      </div>

      <div className="status-tabs">
        {(['all', 'pending', 'active', 'suspended'] as StoreStatus[]).map(s => (
          <button
            key={s}
            type="button"
            className={`status-tab${statusFilter === s ? ' active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s === 'all' ? 'Todas' : s === 'pending' ? 'Pendientes' : s === 'active' ? 'Activas' : 'Suspendidas'}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Cargando ferreterías...</div>
      ) : stores.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏪</div>
          <p>No hay ferreterías con este filtro</p>
        </div>
      ) : (
        <div className="stores-admin-table">
          <div className="stores-admin-header">
            <span>Nombre</span>
            <span>Dirección</span>
            <span>Estado</span>
            <span>Productos</span>
            <span>Pedidos</span>
            <span>Acciones</span>
          </div>
          {stores.map(s => (
            <div key={s.id} className="stores-admin-row">
              <strong>{s.name}</strong>
              <span style={{ color: 'var(--slate)', fontSize: '.9rem' }}>{s.address}</span>
              <span>{statusLabel(s.status)}</span>
              <span>{s.product_count ?? '—'}</span>
              <span>{s.order_count ?? '—'}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {s.status !== 'active' && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ background: 'var(--green)', color: '#fff', border: 'none' }}
                    onClick={() => updateStatus(s.id, 'active')}
                  >
                    Aprobar
                  </button>
                )}
                {s.status !== 'suspended' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost btn-danger"
                    onClick={() => updateStatus(s.id, 'suspended')}
                  >
                    Suspender
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ── Main dashboard ─────────────────────────────────────────────────────────────

export function SuperAdminDashboard() {
  const { user, logout } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>('categories')

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="sidebar-brand">🔧 <span>Ferre</span>Market</div>
        <nav className="sidebar-nav">
          <button
            type="button"
            className={`sidebar-item ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categorías
          </button>
          <button
            type="button"
            className={`sidebar-item ${activeTab === 'stores' ? 'active' : ''}`}
            onClick={() => setActiveTab('stores')}
          >
            Ferreterías
          </button>
        </nav>
        <div className="sidebar-footer">
          <span className="sidebar-user">{user?.fullName}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={logout}>Salir</button>
        </div>
      </aside>

      <main className="admin-main">
        {/* Tab bar for mobile */}
        <div className="tab-bar">
          <button
            type="button"
            className={`tab-bar-item ${activeTab === 'categories' ? 'tab-bar-item--active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categorías
          </button>
          <button
            type="button"
            className={`tab-bar-item ${activeTab === 'stores' ? 'tab-bar-item--active' : ''}`}
            onClick={() => setActiveTab('stores')}
          >
            Ferreterías
          </button>
        </div>

        {activeTab === 'categories' ? <CategoriesTab /> : <StoresTab />}
      </main>
    </div>
  )
}
